/**
 * What the like-with-like filter left out of a comparison, in one line
 * — and, since CU-869fh646w, what it did not filter at all.
 *
 * Pure, so it is tested without mounting anything and used from two
 * places that cannot share a component: the footer of the competitors
 * popup, and the Reason block when there is no popup to open because
 * the filter left nothing to compare against (UX gate, 2026-10-07).
 *
 * The filter judges our own row's kind — the row the engine took, not
 * the product row's axes (Curro, 2026-10-14) — and a strategy says whom
 * it is compared against in one of three modes (CU-869fhuhy6, criterion
 * 12): `same` kind as our product, `every` kind, or a `specific` kind.
 * The event records it as `derivation.compare = { mode, tariff_type,
 * insurance, every_tariff_type, every_insurance }` — the two switches of
 * CU-869fh646w stay for the rows written between the two tasks. With an
 * axis off its counter is 0 by construction and the sentence says why
 * instead of keeping quiet (brief criterion 9). An event written before
 * any of it carries no `compare` and keeps the sentence it always had.
 *
 * Each half is dropped at zero; both at zero, or an event written before
 * the block existed, says nothing — "0 excluded" is noise.
 */

const n = (v) => Number(v || 0)
const offers = (k) => `${k} offer${k === 1 ? '' : 's'}`

/** The counters, named for the axes that still filter. */
function leftOut(sf, { tariffOn, insuranceOn }) {
	if (!sf) return null
	const m = n(sf.excluded_face_mismatch), u = n(sf.excluded_face_unknown)
	const axis = tariffOn ? 'insurance' : insuranceOn ? 'tariff type' : 'insurance or tariff type'
	const parts = []
	if (m > 0) parts.push(`${offers(m)} on a different ${axis}`)
	if (u > 0) parts.push(`${offers(u)} with no ${axis} recorded`)
	return parts.length ? `Left out of this comparison: ${parts.join(' · ')}.` : null
}

//: The three modes. The two switches of the rows in between keep their
//: own sentences below — a historical event reads as it always did.
const modeOf = (compare) => {
	if (!compare) return null
	if (compare.mode === 'every' || compare.mode === 'specific' || compare.mode === 'same') return compare.mode
	return null
}

const specificKind = (compare, names) => {
	const parts = []
	if (compare.tariff_type != null) parts.push(names.prepaidName(Boolean(Number(compare.tariff_type))))
	if (compare.insurance != null) parts.push(names.insuranceName(compare.insurance))
	return parts.join(' · ') || 'any'
}

export function excludedCopy(sf, compare = null, names = null) {
	const mode = modeOf(compare)
	if (mode === 'every') {
		return 'Neither tariff type nor insurance filtered — this strategy compares against every kind of offer.'
	}
	if (mode === 'specific') {
		const kind = names ? specificKind(compare, names) : 'a specific kind'
		const counters = leftOut(sf, { tariffOn: compare.tariff_type == null, insuranceOn: compare.insurance == null })
		return `Compared against a specific kind — ${kind} — whatever our own row sells.${counters ? ` ${counters}` : ''}`
	}
	const tariffOn = !!compare?.every_tariff_type
	const insuranceOn = !!compare?.every_insurance
	if (tariffOn && insuranceOn) {
		return 'Neither tariff type nor insurance filtered — this strategy compares against every kind of offer.'
	}
	const counters = leftOut(sf, { tariffOn, insuranceOn })
	if (tariffOn) {
		return `Tariff type not filtered — this strategy compares against every tariff type.${counters ? ` ${counters}` : ''}`
	}
	if (insuranceOn) {
		return `Insurance not filtered — this strategy compares against every insurance.${counters ? ` ${counters}` : ''}`
	}
	return counters
}

/**
 * The subtitle of the competitors popup: who counted as a competitor.
 *
 * `own` is the row the engine took (`derivation.own`, new events),
 * `sf` the `scope_filter` block (its kind is the row taken too, and the
 * only source on events between 2026-10-08 and this task). Without
 * `compare` the sentence is the one those events always had. Returns
 * `null` when the event records no kind at all.
 */
export function compareCopy(sf, own, compare, names) {
	const tariff = own && own.tariff_type != null ? Boolean(Number(own.tariff_type))
	             : sf && sf.is_prepaid != null ? Boolean(Number(sf.is_prepaid)) : null
	const insurance = own && own.insurance != null ? own.insurance
	                : sf ? sf.insurance : null
	if (!compare) {
		if (!sf) return null
		return `same insurance and tariff type as our own row: ${names.insuranceName(insurance)} · ${names.prepaidName(tariff)}`
	}
	// The three sentences of the selector (CU-869fhuhy6, criterion 12).
	const mode = modeOf(compare)
	if (mode === 'every') return 'every kind'
	if (mode === 'specific') return `a specific kind: ${specificKind(compare, names)}`
	if (mode === 'same') {
		const kind = [tariff == null ? null : names.prepaidName(tariff), insurance == null ? null : names.insuranceName(insurance)].filter(Boolean).join(' · ')
		return `same kind as our product${kind ? ` (${kind})` : ''}`
	}
	const tariffOn = !!compare.every_tariff_type
	const insuranceOn = !!compare.every_insurance
	if (tariffOn && insuranceOn) return 'every tariff type and insurance'
	if (tariffOn) return `every tariff type · same insurance as our own row: ${names.insuranceName(insurance)}`
	if (insuranceOn) return `every insurance · same tariff type as our own row: ${names.prepaidName(tariff)}`
	return `same tariff type and insurance as our own row: ${names.prepaidName(tariff)} · ${names.insuranceName(insurance)}`
}

/**
 * The one-line label of the Strategy column and the Scope block (UX v3
 * D22): `same kind as my product` · `every kind` · `a specific kind: …`.
 * Null for an event written before the compare block existed.
 */
export function compareModeLabel(compare, names) {
	const mode = modeOf(compare)
	if (mode === 'every') return 'every kind'
	if (mode === 'specific') return `a specific kind: ${specificKind(compare, names)}`
	if (mode === 'same') return 'same kind as my product'
	if (compare && compare.every_tariff_type && compare.every_insurance) return 'every kind'
	if (compare && (compare.every_tariff_type || compare.every_insurance)) {
		return compare.every_tariff_type ? 'every tariff type · same insurance' : 'every insurance · same tariff type'
	}
	return null
}

/** The tooltip that goes with it — both sentences the UX gate wrote: what the
 *  counters count, and the one decision an operator would not guess. */
export const EXCLUDED_TOOLTIP = "Offers that passed the strategy's market scope but not our own row's "
	+ 'insurance and tariff type. Partially prepaid offers count as a different tariff type.'
